import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useMotionValue, type MotionValue } from "motion/react";
interface ScrollParallaxResult {
  ref: RefObject<HTMLDivElement | null>;
  value: MotionValue<number>;
}

/**
 * Hook for creating parallax scroll effects on visible elements.
 *
 * The value is a `MotionValue`, not React state: the scroll handler writes it
 * once per frame, and motion forwards it straight to the element's style. React
 * state here re-rendered the whole subtree on every frame of every scroll.
 *
 * @param rangeStart - Starting value for the animation range
 * @param rangeEnd - Ending value for the animation range
 * @returns Object containing ref to attach to element and the animated value
 *
 * @example
 * ```tsx
 * const { ref, value } = useScrollParallax(80, 90);
 * const height = useTransform(value, (v) => `${v}%`);
 * <motion.div ref={ref} style={{ height }} />
 * ```
 */
export function useScrollParallax(rangeStart, rangeEnd) {
  const $ = _c(10);
  const elementRef = useRef(null);
  const value = useMotionValue(rangeStart);
  const [isVisible, setIsVisible] = useState(false);
  const rafId = useRef(undefined);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const element = elementRef.current;
      if (!element) {
        return;
      }
      const observer = new IntersectionObserver(t2 => {
        const [entry] = t2;
        setIsVisible(entry.isIntersecting);
      }, {
        rootMargin: "100px"
      });
      observer.observe(element);
      return () => {
        observer.disconnect();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[2] !== isVisible || $[3] !== rangeEnd || $[4] !== rangeStart || $[5] !== value) {
    t2 = () => {
      const element_0 = elementRef.current;
      if (!element_0 || !isVisible) {
        return;
      }
      const handleScroll = () => {
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
        }
        rafId.current = requestAnimationFrame(() => {
          const rect = element_0.getBoundingClientRect();
          const elementTop = rect.top;
          const elementHeight = rect.height;
          const viewportHeight = window.innerHeight;
          const scrollProgress = Math.max(0, Math.min(1, (viewportHeight - elementTop) / (viewportHeight + elementHeight)));
          value.set(rangeStart + (rangeEnd - rangeStart) * scrollProgress);
        });
      };
      handleScroll();
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      window.addEventListener("resize", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
        }
      };
    };
    t3 = [rangeStart, rangeEnd, isVisible, value];
    $[2] = isVisible;
    $[3] = rangeEnd;
    $[4] = rangeStart;
    $[5] = value;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== value) {
    t4 = {
      ref: elementRef,
      value
    };
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}